import { useEffect, useState } from 'react';
import { PaymentRequest, StripeError } from '@stripe/stripe-js';
import {
  PaymentRequestButtonElement,
  useStripe,
} from '@stripe/react-stripe-js';

import * as api from '../../../api';
import styles from '../index.module.css';

interface Props {
  paymentInfo: api.Payment.PaymentInfo;
  onPaymentError: (error: StripeError) => void;
  onPaymentSuccess: () => void;
}

const BrowserPayElement: React.FC<Props> = ({
  paymentInfo,
  onPaymentError,
  onPaymentSuccess,
}) => {
  const [paymentRequest, setPaymentRequest] = useState<null | PaymentRequest>(
    null,
  );

  const stripe = useStripe();

  useEffect(() => {
    async function makePaymentRequest() {
      if (!stripe) return;
      const { currency, amount } = paymentInfo;
      if (!amount) {
        throw new Error('got a request for a non-existent payment amount');
      }
      const paymentRequest = stripe.paymentRequest({
        country: 'US',
        currency: `${currency}`,
        total: {
          label: 'Co-pay',
          amount,
        },
      });

      if (!paymentRequest) return;
      const canMakePayment = await paymentRequest.canMakePayment();
      if (canMakePayment?.applePay) {
        setPaymentRequest(paymentRequest);
      }
    }

    makePaymentRequest();
  }, [paymentInfo, setPaymentRequest, stripe]);

  useEffect(() => {
    if (!paymentRequest || !stripe) return;
    paymentRequest.on('paymentmethod', async (ev) => {
      const { error: confirmError } = await stripe.confirmCardPayment(
        paymentInfo.clientSecret,
        { payment_method: ev.paymentMethod.id },
        { handleActions: false },
      );

      if (confirmError) {
        onPaymentError(confirmError);
        return ev.complete('fail');
      }

      ev.complete('success');
      onPaymentSuccess();
    });
  }, [
    onPaymentError,
    onPaymentSuccess,
    paymentInfo.clientSecret,
    paymentRequest,
    stripe,
  ]);

  if (!paymentRequest) return null;

  return (
    <div className={styles.BrowserPayContainer}>
      <PaymentRequestButtonElement options={{ paymentRequest }} />
      <div className={styles.CenterText}>
        Or enter your payment details below
      </div>
    </div>
  );
};

export default BrowserPayElement;
